import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { env } from "@/config/env";
import { getAccountId, restoreAccountId } from "@/lib/auth/account";
import { parseFieldErrorsFromApiData } from "@/lib/http/fieldErrors";
import { parseAccessReasonFromApiData } from "@/lib/http/access-details";
import { parseBillingReasonFromApiData } from "@/lib/http/billing-details";
import { redirectToAppUninstalled } from "@/lib/auth/appUninstalledRedirect";
import { StudioApiError } from "@/types/api";
import type { ApiResponse } from "@/types/api";

function toStudioApiError(
  body: Partial<ApiResponse<unknown>>,
  status?: number,
): StudioApiError {
  return new StudioApiError(
    body.code ?? "UNKNOWN",
    body.message ?? "Request failed",
    status,
    parseFieldErrorsFromApiData(body.code, body.data),
    parseBillingReasonFromApiData(body.code, body.data),
    parseAccessReasonFromApiData(body.code, body.data),
  );
}

function maybeRedirectForAccessReason(error: StudioApiError): void {
  if (error.accessReason === "app_uninstalled") {
    redirectToAppUninstalled();
  }
}

function normalizeBaseUrl(base: string): string {
  return base.replace(/\/$/, "");
}

export const http: AxiosInstance = axios.create({
  baseURL: normalizeBaseUrl(env.apiBaseUrl),
  timeout: 60_000,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

http.interceptors.request.use((config) => {
  const accountId = getAccountId() ?? restoreAccountId();
  if (accountId?.startsWith("acct_")) {
    config.headers.set("X-Stripe-Account-Id", accountId);
  }
  if (config.data && !config.headers.has("Content-Type")) {
    config.headers.set("Content-Type", "application/json");
  }
  return config;
});

http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => {
    const body = response.data;
    if (body && body.success === false) {
      const err = toStudioApiError(body, response.status);
      maybeRedirectForAccessReason(err);
      throw err;
    }
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error) && error.response?.data) {
      const body = error.response.data as Partial<ApiResponse<unknown>>;
      if (body.code || body.message) {
        const err = toStudioApiError(body, error.response.status);
        maybeRedirectForAccessReason(err);
        throw err;
      }
    }
    throw error;
  },
);

/** 解包成功信封的 data 字段 */
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await http.get<ApiResponse<T>>(url, { params });
  return data.data;
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await http.post<ApiResponse<T>>(url, body);
  return data.data;
}
